import React, { useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, LabelList
} from "recharts";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useExcelExport } from "../../../../CustomHooks/useExcelExport";

// Puedes usar styled-components o CSS modules para mejor diseño
const chartContainerStyle = {
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  padding: "1.5rem",
  // margin: "2rem 0",
  width: "100%",
  height: "fit-content",
};

const titleStyle = {
  fontSize: "1.5rem",
  fontWeight: 700,
  marginBottom: "1rem",
  color: "#2c3e50",
};

const buttonStyle = {
  marginTop: "1rem",
};

// Regresión lineal simple para la línea de tendencia
function getTrendLineData(data, dataKey) {
  if (!data || data.length < 2) return [];
  const n = data.length;
  const xs = data.map((_, i) => i + 1);
  const ys = data.map(d => Number(d[dataKey]) || 0);
  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((sum, x, i) => sum + x * ys[i], 0);
  const sumXX = xs.reduce((sum, x) => sum + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return xs.map((x, i) => ({
    ...data[i],
    trend: slope * x + intercept
  }));
}

// Objeto de configuración editable para la apariencia de la gráfica
export const trendChartConfig = {
  showLabelsThreshold: 100, // Máximo de puntos para mostrar los números en la gráfica
  fontSizeLabel: 12, // Tamaño de fuente de los números
  fontWeightLabel: 700, // Grosor de fuente de los números
  colorLabel: "#22415eff", // Color de los números
  colorLine: "#223A5E", // Color de la línea principal
  colorTrend: "#E94F37", // Color de la línea de tendencia
  colorPoint: "#2AB7CA", // Color de los puntos
  colorAxis: "#444", // Color de los ejes y ticks
  lineWidth: 3, // Grosor de la línea principal
  lineWidthMany: 2, // Grosor de la línea si hay muchos datos
  pointRadius: 5, // Radio de los puntos
  pointRadiusActive: 7, // Radio del punto activo
  chartHeight: 300, // Altura de la gráfica
  showAlternateLabels: true, // Si es true, muestra valores alternos cuando se supera el umbral
 
};

// Utilidad para obtener el lapso de tiempo (solo número para el eje X)
function getLapsoNumero(d) {
  if (d.SEMANA !== undefined) return d.SEMANA;
  if (d.MES !== undefined) return d.MES;
  if (d.ANO !== undefined) return d.ANO;
  return "";
}

// Utilidad para obtener el tipo de lapso (para el label del eje X)
function getLapsoTipo(data) {
  if (!Array.isArray(data) || data.length === 0) return "";
  const d = data[0];
  if (d.SEMANA !== undefined) return "Semana";
  if (d.MES !== undefined) return "Mes";
  if (d.ANO !== undefined) return "Año";
  return "";
}

// Utilidad para obtener el lapso de tiempo (SEMANA, MES, AÑO) en formato amigable
function getLapsoTiempo(d) {
  if (d.SEMANA !== undefined && d.ANO !== undefined)
    return `Semana ${d.SEMANA}`;
  if (d.MES !== undefined && d.ANO !== undefined)
    return `Mes ${d.MES}`;
  if (d.SEMANA !== undefined)
    return `Semana ${d.SEMANA}`;
  if (d.MES !== undefined)
    return `Mes ${d.MES}`;
  if (d.ANO !== undefined)
    return `Año ${d.ANO}`;
  return "";
}

// Utilidad para obtener dinámicamente las columnas de la tabla según la data
function getDynamicColumns(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const keys = Object.keys(data[0]);
  const cols = [];
  // Siempre mostrar TOTAL primero si existe
  if (keys.includes("TOTAL")) cols.push({ field: "TOTAL", header: "Total" });
  // Mostrar SEMANA si existe
  if (keys.includes("SEMANA")) cols.push({ field: "SEMANA", header: "Semana" });
  // Mostrar MES solo si existe y NO existe SEMANA (para evitar confusión en datos con SEMANA)
  if (keys.includes("MES") && !keys.includes("SEMANA")) cols.push({ field: "MES", header: "Mes" });
  // Mostrar ANO o AÑO si existen
  if (keys.includes("ANO")) cols.push({ field: "ANO", header: "Año" });
  if (keys.includes("AÑO")) cols.push({ field: "AÑO", header: "Año" });
  // Agrega cualquier otro campo que no sea TOTAL, SEMANA, MES, ANO, AÑO
  keys.forEach(k => {
    if (!["TOTAL", "SEMANA", "MES", "ANO", "AÑO"].includes(k)) {
      cols.push({ field: k, header: k.replace(/_/g, " ") });
    }
  });
  return cols;
}

// Label personalizado: valor centrado en el punto, configurable por props
const CustomValueLabel = (props) => {
  const { x, y, value, index, imprimirCada, colorTexto, mostrarNumeros = true } = props;
  // Si mostrarNumeros es false, no mostrar nada
  if (!mostrarNumeros) return null;
  // Siempre mostrar el texto, solo controlar la impresión alterna si imprimirCada está definido
  if (imprimirCada && Number(imprimirCada) > 1) {
    if (index % Number(imprimirCada) !== 0) return null;
  }
  let displayValue = value;
  if (typeof value === "number") {
    displayValue = value % 1 === 0 ? value : value.toFixed(2);
  } else if (!isNaN(Number(value))) {
    displayValue = Number(value) % 1 === 0 ? value : Number(value).toFixed(2);
  }
  return (
    <>
      <g>
        <text
          x={x}
          y={y - 10}
          textAnchor="middle"
          fontSize={trendChartConfig.fontSizeLabel}
          fontWeight={trendChartConfig.fontWeightLabel}
          fill="#fff"
          stroke="#fff"
          strokeWidth={2}
          strokeLinejoin="round"
          style={{
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {displayValue}
        </text>
        <text
          x={x}
          y={y - 10}
          textAnchor="middle"
          fontSize={trendChartConfig.fontSizeLabel}
          fontWeight={trendChartConfig.fontWeightLabel}
          fill={colorTexto || trendChartConfig.colorLabel}
          style={{
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {displayValue}
        </text>
      </g>
    </>
  );
};

export const TrendChart = ({
  data = [],
  title = "Tendencia",
  dataKey = "TOTAL",
  xKeyAno = "ANO",
  xKeyMes = "MES",
  columns,
  excelFileName = "Tendencia",
  config = trendChartConfig,
  imprimirCada,
  mostrarPuntos = true,
  colorTexto,
  mostrarNumeros = true, // Nuevo prop para mostrar/ocultar números en la gráfica
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const { exportarAExcel } = useExcelExport();

  // Columnas dinámicas si no se pasan por props
  const dynamicColumns = useMemo(
    () => columns && columns.length > 0 ? columns : getDynamicColumns(data),
    [columns, data]
  );

  const chartData = useMemo(
    () =>
      data
        .map((d) => {
          let totalValue = d[dataKey] !== undefined ? d[dataKey] : d.TOTAL !== undefined ? d.TOTAL : "";
          if (!isNaN(Number(totalValue))) {
            totalValue = Number(totalValue) % 1 === 0 ? Number(totalValue) : Number(totalValue).toFixed(2);
          }
          // El label para el eje X será solo el número (semana, mes, año)
          return {
            ...d,
            label: getLapsoNumero(d),
            total: totalValue,
          };
        })
        .sort((a, b) => {
          // Ordena por año, semana o mes si existen
          const aAno = a.ANO !== undefined ? a.ANO : 0;
          const bAno = b.ANO !== undefined ? b.ANO : 0;
          const aSemana = a.SEMANA !== undefined ? a.SEMANA : 0;
          const bSemana = b.SEMANA !== undefined ? b.SEMANA : 0;
          const aMes = a.MES !== undefined ? a.MES : 0;
          const bMes = b.MES !== undefined ? b.MES : 0;
          if (aAno !== bAno) return aAno - bAno;
          if (aSemana !== bSemana) return aSemana - bSemana;
          return aMes - bMes;
        }),
    [data, dataKey]
  );

  const trendData = useMemo(() => getTrendLineData(chartData, dataKey), [chartData, dataKey]);

  // Calcular línea de tendencia (media simple)
  const avg =
    chartData.length > 0
      ? chartData.reduce((sum, d) => sum + (d[dataKey] || 0), 0) / chartData.length
      : 0;

  // Filtro global para la tabla (ahora usa dynamicColumns para asegurar compatibilidad)
  const filteredData = useMemo(() => {
    if (!globalFilter) return data;
    const filter = globalFilter.toLowerCase();
    return data.filter((row) =>
      dynamicColumns.some((col) =>
        String(row[col.field] ?? "")
          .toLowerCase()
          .includes(filter)
      )
    );
  }, [data, globalFilter, dynamicColumns]);

  // Para saber si hay muchos datos
  const showLabels = (trendData.length ? trendData : chartData).length <= config.showLabelsThreshold;

  return (
    <div style={chartContainerStyle}>
      <div style={titleStyle}>{title}</div>
      <div style={{ width: "100%", height: `${config.chartHeight}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData.length ? trendData : chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e6ed" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: config.colorAxis }}
              interval={0} // Fuerza a mostrar todos los labels del eje X
              minTickGap={0} // Reduce el espacio mínimo entre ticks
              padding={{ left: 30, right: 40 }} // Agrega espacio extra a los lados
              label={{
                value: getLapsoTipo(chartData),
                position: "insideLeft",
                offset: 0,
                fontSize: 13,
                angle: 0,
                dy: 30,
                dx: -30,
                style: { textAnchor: "start", fontWeight: "bold" }
              }}
            />
            <YAxis
              dataKey="total"
              tick={{ fontSize: 11, fill: config.colorAxis }}
              label={{ value: "Total", angle: -90, position: "insideLeft", fontSize: 13 }}
              tickFormatter={v =>
                typeof v === "number"
                  ? v % 1 === 0
                    ? v
                    : v.toFixed(2)
                  : (!isNaN(Number(v)) && Number(v) % 1 !== 0)
                    ? Number(v).toFixed(2)
                    : v
              }
              axisLine={{ stroke: config.colorAxis }}
              tickLine={{ stroke: config.colorAxis }}
              allowDataOverflow={true} // Permite mostrar más allá del rango
              domain={['auto', 'auto']} // Ajusta el dominio automáticamente
              interval={0} // Muestra todos los ticks posibles
              padding={{ top: 40, bottom: 40 }} // Agrega espacio extra arriba y abajo
            />
            <Legend />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={config.colorLine}
              strokeWidth={config.lineWidth}
              dot={mostrarPuntos ? { r: config.pointRadius, fill: config.colorPoint, stroke: "none" } : false}
              activeDot={mostrarPuntos ? { r: config.pointRadiusActive, fill: config.colorPoint, stroke: "none" } : false}
            >
              <LabelList
                dataKey={dataKey}
                content={
                  (props) => (
                    <CustomValueLabel
                      {...props}
                      imprimirCada={imprimirCada}
                      mostrarPuntos={true}
                      colorTexto={colorTexto}
                      mostrarNumeros={mostrarNumeros}
                      data={trendData.length ? trendData : chartData}
                    />
                  )
                }
              />
            </Line>
            <Line
              type="linear"
              dataKey="trend"
              stroke={config.colorTrend}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              name="Tendencia"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Button
        label="Ver información"
        icon="pi pi-table"
        style={buttonStyle}
        onClick={() => setModalVisible(true)}
        className="p-button-rounded p-button-info"
      />
      <Dialog
        header={`Datos de ${title}`}
        visible={modalVisible}
        style={{ width: "90vw", maxWidth: 800 }}
        onHide={() => setModalVisible(false)}
        footer={
          <div>
            <Button
              label="Descargar Excel"
              icon="pi pi-file-excel"
              className="p-button-success"
              onClick={() => exportarAExcel(filteredData, dynamicColumns, excelFileName)}
            />
          </div>
        }
        modal
      >
        <div className="p-inputgroup" style={{ marginBottom: 16 }}>
          <span className="p-inputgroup-addon">
            <i className="pi pi-search" />
          </span>
          <InputText
            placeholder="Buscar..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
        <DataTable
          value={filteredData}
          paginator
          rows={10}
          responsiveLayout="scroll"
          emptyMessage="No hay datos"
        >
          {dynamicColumns.map((col) => (
            <Column key={col.field} field={col.field} header={col.header} sortable />
          ))}
        </DataTable>
      </Dialog>
    </div>
  );
};
